import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from 'i18n/i18n.types';
import cloneDeep from 'lodash.clonedeep';
import { FormTemplate, FormTemplateSection, FormTemplateValidator } from 'data/template/template.types';
import { useFieldCreateNew } from 'routes/TargetDomain/hooks/useFieldCreateNew';
import Form, { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayout } from 'components/FormLayout';
import * as types from '../TargetOptions.types';

export type FormDataType = {
  LOC_POLL_FREQ?: string,
  domain?: string,
};

type Props = {
  template: FormTemplate,
  i18nKeyPrefix: string,
  data: Partial<FormDataType>
  onFieldUpdate?: (param: FieldUpdateHandlerParam) => void
}

export const NEWORK_FORM_I18N_KEY = 'network.form';

export const TargetOptionsForm = forwardRef<types.Ref, Props>(({
  template,
  i18nKeyPrefix,
  data,
  onFieldUpdate = () => null,
}: Props, ref) => {
  const mainRef = useRef<Form>(null);
  const { t } = useTranslation();
  const [handleFieldCreateNew] = useFieldCreateNew(
    (param) => handleFieldUpdate(param),
  );

  const [formData, setFormData] = useState(data);

  useEffect(() => {
    setFormData(cloneDeep(data));
  }, [data]);

  const isValid = (forceValidation = false, forceTouched = true) => {
    const isMainValid = mainRef.current?.isValid(forceValidation, forceTouched) ?? true;
    return isMainValid;
  };

  const getValues = () => {
    const {
      ...values
    } = mainRef.current?.getValues();

    const formValues: types.TargetOption = {
      ...values,
    };

    return formValues;
  };

  const handleFieldUpdate = (param: FieldUpdateHandlerParam): void => {
    onFieldUpdate(param);

    if (param.name === 'domain') {
      template.formId = Date.now().toString();
      setFormData((current) => ({
        ...current,
        [param.name]: param.value,
      }));
    }
  };

  const invalidLocPollFreqValueValidator = (
    _propFields?: unknown,
    _stateFields?: unknown,
    value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
  ) => {
    const valueToNumber = Number(value);
    const valid = valueToNumber >= 0 && valueToNumber <= 99999999 && valueToNumber % 30 === 0;

    let message = '';
    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  };

  const customValidationFunctionMap = {
    invalidLocPollFreqValueValidator
  };

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
  }));

  return (
    <div className="target-options-form" data-testid="TargetOptionsForm">
      <FormLayout>
        <Form
          ref={mainRef}
          name={template.formId ?? template.key}
          defaults={formData}
          fields={(template.main as FormTemplateSection).fields}
          layout={(template.main as FormTemplateSection).metadata.layout?.rows}
          i18nKeyPrefix={i18nKeyPrefix}
          customValidationFunctionMap={customValidationFunctionMap}
          fieldUpdateHandler={handleFieldUpdate}
          fieldCreateNewHandler={handleFieldCreateNew}
        />
      </FormLayout>
    </div>
  );
});

export default TargetOptionsForm;
